import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../api';
import type { Tarif } from '../../billtoro/types';
import { fehlendeFunktion, upgradeAnbieten } from '../../billtoro/upgrade';
import { ConfirmDialog, Dialog } from '../../components/Dialog';
import { useToast } from '../../lib/toast';
import { fehlerText, GesperrtKnopf } from '../tarifHinweis';
import type { Kunde } from '../types';
import { dauerLesen, dauerText, stundenText } from './dauer';
import { KundeSuche, TextVorschlag, type ZeitVorschlaege } from './ZeitDialog';
import { uhrStartUrl, uhrUrl, type Uhr, type Zeit } from './ZeitDaten';
import './zeiten.css';

// Die Uhr (eine je Login und Firma, Startzeit auf dem Server): starten ohne Pflichtfelder, Kunde, Tätigkeit und Objekt
// ändern, während sie läuft (gespeichert beim Verlassen des Feldes), stoppen (daraus wird eine Zeit) oder verwerfen.
// Die Dauer zählt im Browser jede Sekunde, gerechnet nach der Uhr des Servers (Abstand aus „jetzt“ der Antworten).
// UhrKarte auf der Seite Zeiten und in der Übersicht, UhrChip in der Kopfzeile, UhrStarten als Knopf.

// Abstand der Uhr des Geräts zur Uhr des Servers, gesetzt bei jeder Antwort mit „jetzt“ (Sammelantwort, Liste der
// Zeiten); gilt für alle Uhren auf dieser Seite
let abstand = 0;
export function serverZeitMerken(jetzt: string | null | undefined) {
  const t = jetzt ? Date.parse(jetzt) : NaN;
  if (Number.isFinite(t)) abstand = t - Date.now();
}

// Zeit seit dem Start in Millisekunden nach der Uhr des Servers, jede Sekunde neu
export function useLaufzeit(laeuftSeit: string | undefined): number {
  const [, setTakt] = useState(0);
  useEffect(() => {
    if (!laeuftSeit) return;
    const t = window.setInterval(() => setTakt((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, [laeuftSeit]);
  return laeuftSeit ? Math.max(0, Date.now() + abstand - Date.parse(laeuftSeit)) : 0;
}

// Kopfzeile: „● 0:42“, führt zur Seite Zeiten (nur die eigene Uhr der gewählten Firma)
export function UhrChip({ zeiten }: { zeiten: { uhr: Uhr | null; jetzt: string } | null | undefined }) {
  const ms = useLaufzeit(zeiten?.uhr?.laeuftSeit);
  if (!zeiten?.uhr) return null;
  return (
    <Link to="/rechnung/zeiten" className="rt-uhr-chip" title="Ihre Uhr läuft">
      <span aria-hidden="true" className="rt-uhr-punkt">
        ●
      </span>
      <span className="sr-only">Uhr läuft: </span>
      <span className="num">{dauerText(Math.floor(ms / 60_000))}</span>
    </Link>
  );
}

// „Uhr starten“ (ohne die Funktion im Tarif: Marke „Start“, öffnet „Mehr mit Billtoro“). Läuft die Uhr schon auf einem
// anderen Gerät, kommt sie mit der Antwort (409 uhr_laeuft) und wird gezeigt.
export function UhrStarten({
  firmId,
  tarif,
  className = 'btn',
  onUhr,
}: {
  firmId: string;
  tarif: Tarif | null | undefined;
  className?: string;
  onUhr(u: Uhr): void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const fehlt = fehlendeFunktion(tarif, 'zeiten');
  if (fehlt) {
    return (
      <GesperrtKnopf tarif={tarif} fehlt={fehlt} className={className} onUpgrade={() => upgradeAnbieten(tarif, 'zeiten')}>
        Uhr starten
      </GesperrtKnopf>
    );
  }
  async function starten() {
    setBusy(true);
    try {
      onUhr(await api.post<Uhr>(uhrStartUrl(firmId), {}));
    } catch (err) {
      const laeuft = err instanceof ApiError && err.code === 'uhr_laeuft' ? (err.data?.details as { uhr?: Uhr } | undefined)?.uhr : undefined;
      if (laeuft) {
        toast('Ihre Uhr läuft schon.');
        onUhr(laeuft);
      } else {
        const t = fehlerText(err);
        if (t) toast(t);
      }
    } finally {
      setBusy(false);
    }
  }
  return (
    <button type="button" className={className} disabled={busy} onClick={() => void starten()}>
      Uhr starten
    </button>
  );
}

// Laufende eigene Uhr: Dauer, Kunde, Tätigkeit, Objekt, „Stoppen“ und „Verwerfen“. onUhr: Uhr geändert oder weg (null);
// onGestoppt: daraus ist eine Zeit geworden.
export function UhrKarte({
  uhr,
  kunden,
  vorschlaege,
  klein = false,
  onUhr,
  onGestoppt,
}: {
  uhr: Uhr;
  kunden: Kunde[];
  vorschlaege?: ZeitVorschlaege;
  // Übersicht: ohne den Satz unten
  klein?: boolean;
  onUhr(u: Uhr | null): void;
  onGestoppt(z: Zeit): void;
}) {
  const toast = useToast();
  const ms = useLaufzeit(uhr.laeuftSeit);
  const [kundeId, setKundeId] = useState(uhr.kunde?.id ?? null);
  const [taetigkeit, setTaetigkeit] = useState(uhr.taetigkeit ?? '');
  const [objekt, setObjekt] = useState(uhr.objekt ?? '');
  const [frage, setFrage] = useState<'kurz' | 'verwerfen' | { lang: number } | null>(null);
  const [langDauer, setLangDauer] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // gespeicherter Stand (Kunde, Tätigkeit, Objekt) und die laufende Anfrage dazu: „Stoppen“ wartet darauf
  const stand = useRef({ kundeId: uhr.kunde?.id ?? null, taetigkeit: uhr.taetigkeit ?? '', objekt: uhr.objekt ?? '' });
  const laeuft = useRef<Promise<void> | null>(null);

  // andere Uhr (z. B. auf einem zweiten Gerät gestartet): Felder neu
  useEffect(() => {
    setKundeId(uhr.kunde?.id ?? null);
    setTaetigkeit(uhr.taetigkeit ?? '');
    setObjekt(uhr.objekt ?? '');
    stand.current = { kundeId: uhr.kunde?.id ?? null, taetigkeit: uhr.taetigkeit ?? '', objekt: uhr.objekt ?? '' };
    // nur bei einer anderen Uhr
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uhr.id]);

  // Uhr weg (auf einem anderen Gerät gestoppt oder verworfen): Karte zu, Liste neu
  function weg(err: unknown): boolean {
    if (err instanceof ApiError && (err.status === 404 || err.code === 'uhr_aus')) {
      toast('Diese Uhr läuft nicht mehr.');
      onUhr(null);
      return true;
    }
    return false;
  }

  // Kunde, Tätigkeit und Objekt speichern, wenn sie sich geändert haben
  function speichern(neu: Partial<typeof stand.current>): Promise<void> {
    const w = { ...stand.current, ...neu };
    const s = stand.current;
    if (w.kundeId === s.kundeId && w.taetigkeit.trim() === s.taetigkeit.trim() && w.objekt.trim() === s.objekt.trim()) return laeuft.current ?? Promise.resolve();
    stand.current = w;
    const p = (laeuft.current ?? Promise.resolve()).then(async () => {
      try {
        const u = await api.put<Uhr>(uhrUrl(uhr.id), { kundeId: w.kundeId, taetigkeit: w.taetigkeit.trim() || null, objekt: w.objekt.trim() || null });
        setFehler(null);
        onUhr({ ...u, laeuftSeit: u.laeuftSeit ?? uhr.laeuftSeit });
      } catch (err) {
        if (!weg(err)) setFehler(fehlerText(err));
      }
    });
    laeuft.current = p;
    return p;
  }

  async function stoppen(minuten?: number) {
    setBusy(true);
    setFehler(null);
    try {
      await speichern({ taetigkeit, objekt });
      const z = await api.post<Zeit>(`${uhrUrl(uhr.id)}/stopp`, minuten ? { minuten } : {});
      setFrage(null);
      toast(`Zeit gespeichert (${stundenText(z.minuten)})${z.kunde ? '' : '. Bitte noch einen Kunden eintragen.'}`);
      onGestoppt(z);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'uhr_lang') {
        const m = Number((err.data?.details as { minuten?: number } | undefined)?.minuten ?? 0);
        setLangDauer('');
        setFrage({ lang: m });
      } else if (!weg(err)) {
        setFrage(null);
        setFehler(fehlerText(err));
      }
    } finally {
      setBusy(false);
    }
  }

  async function verwerfen() {
    setBusy(true);
    try {
      await api.del(uhrUrl(uhr.id));
      setFrage(null);
      toast('Uhr verworfen');
      onUhr(null);
    } catch (err) {
      if (!weg(err)) setFehler(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }

  const lang = typeof frage === 'object' && frage ? frage.lang : null;
  const langMinuten = dauerLesen(langDauer);
  const tage = lang ? Math.floor(lang / 1440) : 0;

  return (
    <section className={`rt-card zt-uhr${klein ? ' klein' : ''}`} aria-label="Uhr">
      <p className="zt-uhr-kopf">
        <span className="zt-uhr-punkt" aria-hidden="true">
          ●
        </span>{' '}
        Uhr läuft · <b className="num zt-uhr-zeit">{dauerText(Math.floor(ms / 60_000))}</b>
      </p>
      <div className="zt-uhr-felder">
        <div className="field">
          <span id={`zt-uk-${uhr.id}`}>Kunde</span>
          <KundeSuche
            kunden={kunden}
            kundeId={kundeId}
            labelId={`zt-uk-${uhr.id}`}
            label="Kunde der Uhr"
            onChange={(id) => {
              setKundeId(id);
              void speichern({ kundeId: id });
            }}
          />
        </div>
        <label className="field">
          <span>Tätigkeit</span>
          <TextVorschlag
            value={taetigkeit}
            onChange={setTaetigkeit}
            vorschlaege={vorschlaege?.taetigkeiten ?? []}
            placeholder="z. B. Montage"
            maxLength={200}
            onBlur={(v) => void speichern({ taetigkeit: v })}
          />
        </label>
        <label className="field">
          <span>Einsatzort oder Objekt</span>
          <TextVorschlag
            value={objekt}
            onChange={setObjekt}
            vorschlaege={vorschlaege?.objekte ?? []}
            placeholder="optional"
            maxLength={200}
            onBlur={(v) => void speichern({ objekt: v })}
          />
        </label>
      </div>
      {fehler && <div className="notice bad">{fehler}</div>}
      <div className="zt-uhr-act">
        <button type="button" className="btn" disabled={busy} onClick={() => setFrage('verwerfen')}>
          Verwerfen
        </button>
        <button type="button" className="btn primary zt-stopp" disabled={busy} onClick={() => (ms < 60_000 ? setFrage('kurz') : void stoppen())}>
          Stoppen
        </button>
      </div>
      {!klein && <p className="rt-hint zt-klein">Die Uhr läuft weiter, auch wenn Sie Billtoro schließen.</p>}

      <ConfirmDialog
        open={frage === 'verwerfen'}
        title="Uhr verwerfen?"
        text="Die Zeit wird nicht gespeichert."
        confirmLabel="Verwerfen"
        danger
        busy={busy}
        onConfirm={() => void verwerfen()}
        onCancel={() => setFrage(null)}
      />
      <Dialog open={frage === 'kurz'} onClose={() => setFrage(null)} title="Die Uhr lief weniger als eine Minute. Verwerfen?">
        <div className="dlg-actions">
          <button type="button" className="btn" disabled={busy} onClick={() => void stoppen(1)}>
            Als 1 Minute speichern
          </button>
          <button type="button" className="btn dark" disabled={busy} autoFocus onClick={() => void verwerfen()}>
            Verwerfen
          </button>
        </div>
      </Dialog>
      <Dialog
        open={lang !== null}
        onClose={() => setFrage(null)}
        title={`Die Uhr läuft seit ${tage >= 2 ? `${tage} Tagen` : `${Math.floor((lang ?? 0) / 60)} Stunden`}. Wie lange haben Sie gearbeitet?`}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (langMinuten !== null) void stoppen(langMinuten);
          }}
        >
          <label className="field">
            <span>Dauer</span>
            <input value={langDauer} inputMode="decimal" placeholder="z. B. 8 oder 7:30" autoFocus onChange={(e) => setLangDauer(e.target.value)} />
            <small>Höchstens 24 Stunden je Eintrag.</small>
          </label>
          <div className="dlg-actions">
            <button type="button" className="btn" onClick={() => setFrage(null)}>
              Abbrechen
            </button>
            <button type="submit" className="btn primary" disabled={busy || langMinuten === null}>
              Speichern
            </button>
          </div>
        </form>
      </Dialog>
    </section>
  );
}

// Laufende Uhr einer anderen Person (nur wer alle Zeiten sieht): ohne Start und Dauer, „Stoppen“ oder „Verwerfen“
export function FremdeUhr({ id, name, onFertig }: { id: string; name: string; onFertig(z: Zeit | null): void }) {
  const toast = useToast();
  const [frage, setFrage] = useState<'verwerfen' | { lang: number } | null>(null);
  const [dauer, setDauer] = useState('');
  const [busy, setBusy] = useState(false);
  const minuten = dauerLesen(dauer);

  async function stoppen(m?: number) {
    setBusy(true);
    try {
      const z = await api.post<Zeit>(`${uhrUrl(id)}/stopp`, m ? { minuten: m } : {});
      setFrage(null);
      toast(`Zeit gespeichert (${stundenText(z.minuten)})`);
      onFertig(z);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'uhr_lang') {
        setDauer('');
        setFrage({ lang: Number((err.data?.details as { minuten?: number } | undefined)?.minuten ?? 0) });
      } else {
        setFrage(null);
        const t = fehlerText(err);
        if (t) toast(t);
        onFertig(null);
      }
    } finally {
      setBusy(false);
    }
  }
  async function verwerfen() {
    setBusy(true);
    try {
      await api.del(uhrUrl(id));
      toast('Uhr verworfen');
    } catch (err) {
      const t = fehlerText(err);
      if (t) toast(t);
    } finally {
      setBusy(false);
      setFrage(null);
      onFertig(null);
    }
  }
  const lang = typeof frage === 'object' && frage ? frage.lang : null;
  return (
    <li className="zt-fremd">
      <span>
        <span className="zt-uhr-punkt" aria-hidden="true">
          ●
        </span>{' '}
        Uhr läuft · {name}
      </span>
      <span className="zt-fremd-act">
        <button type="button" className="linkbtn" disabled={busy} onClick={() => void stoppen()}>
          Stoppen
        </button>
        <button type="button" className="linkbtn rt-danger" disabled={busy} onClick={() => setFrage('verwerfen')}>
          Verwerfen
        </button>
      </span>
      <ConfirmDialog
        open={frage === 'verwerfen'}
        title={`Uhr von ${name} verwerfen?`}
        text="Die Zeit wird nicht gespeichert."
        confirmLabel="Verwerfen"
        danger
        busy={busy}
        onConfirm={() => void verwerfen()}
        onCancel={() => setFrage(null)}
      />
      <Dialog open={lang !== null} onClose={() => setFrage(null)} title={`Die Uhr läuft seit mehr als 24 Stunden. Wie lange hat ${name} gearbeitet?`}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (minuten !== null) void stoppen(minuten);
          }}
        >
          <label className="field">
            <span>Dauer</span>
            <input value={dauer} inputMode="decimal" placeholder="z. B. 8 oder 7:30" autoFocus onChange={(e) => setDauer(e.target.value)} />
            <small>Höchstens 24 Stunden je Eintrag.</small>
          </label>
          <div className="dlg-actions">
            <button type="button" className="btn" onClick={() => setFrage(null)}>
              Abbrechen
            </button>
            <button type="submit" className="btn primary" disabled={busy || minuten === null}>
              Speichern
            </button>
          </div>
        </form>
      </Dialog>
    </li>
  );
}
